import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

/**
 * AI ghost-text suggestions for the booking form (CONEK-4417) — Hotwire parity
 * with the React AiFormProvider + AiGhostInput.
 *
 * Debounces form changes, POSTs the field context to the same backend the React
 * form uses (/api/v1/ai/context-suggestions), and renders each returned
 * suggestion as gray ghost text INSIDE the Title / Location inputs (an absolute
 * overlay — never a popover/dropdown, so nothing clips inside the booking modal
 * or on a narrow mobile screen).
 *
 * Accept the ghost with Tab (desktop) or by tapping the hint badge (touch — no
 * Tab key). Typing or Escape dismisses it. Accept/dismiss report back to
 * /api/v1/ai/feedback (best-effort). The controller no-ops unless `enabledValue`
 * is true (tenant AI flag + ai.use permission, decided server-side).
 *
 * Markup contract (booking_form_component.html.erb):
 *   <form data-controller="… pipeline--booking-ai"
 *         data-pipeline--booking-ai-enabled-value="true"
 *         data-pipeline--booking-ai-accept-tab-value="Tab to accept"
 *         data-pipeline--booking-ai-accept-tap-value="Tap to accept">
 *
 * The Hotwire opportunity drawer reuses it (CONEK-7690 — React's OpportunityForm
 * wrapped its Details textarea in AiGhostInput under
 * `<AiFormProvider entityType="Opportunity" context={company_id, stage,
 * contact_person_id}>`): `entity-type-value="Opportunity"` switches the entity,
 * the ghosted field (`remarks`, a textarea) and the context to that form's.
 */

const SUGGEST_URL = "/api/v1/ai/context-suggestions"
const FEEDBACK_URL = "/api/v1/ai/feedback"
const DEBOUNCE_MS = 450
// Per entity: the form's param scope and the plain-text fields React ghosted.
// Booking: Title + Location (its Description is Lexxy rich text — the React form
// doesn't ghost it either). Opportunity: the Details textarea (`remarks`).
const ENTITIES = {
  Booking: { scope: "booking", fields: ["title", "location"] },
  Opportunity: { scope: "opportunity", fields: ["remarks"] },
} as const

type EntityType = keyof typeof ENTITIES
type GhostFieldName = string

interface FieldState {
  input: HTMLInputElement | HTMLTextAreaElement
  ghost: HTMLDivElement
  ghostText: HTMLSpanElement
  hint: HTMLButtonElement
}

interface SuggestionEntry {
  suggestion: string
}

interface SuggestionsResponse {
  suggestions?: Partial<Record<GhostFieldName, SuggestionEntry>>
}

export default class extends Controller {
  static values = {
    enabled: Boolean,
    acceptTab: String,
    acceptTap: String,
    entityType: { type: String, default: "Booking" },
  }

  declare enabledValue: boolean
  declare acceptTabValue: string
  declare acceptTapValue: string
  declare entityTypeValue: string

  private get entity(): EntityType {
    return this.entityTypeValue in ENTITIES
      ? (this.entityTypeValue as EntityType)
      : "Booking"
  }

  private get ghostFields(): readonly string[] {
    return ENTITIES[this.entity].fields
  }

  private suggestions: Partial<Record<GhostFieldName, SuggestionEntry>> = {}
  private fields: Partial<Record<GhostFieldName, FieldState>> = {}
  private _debounce: ReturnType<typeof setTimeout> | null = null
  private _touch = false
  // Stable bound reference for add/removeEventListener (set in connect from the
  // _onContextChange method). Distinct name avoids a duplicate class member.
  private _boundOnContextChange!: () => void

  connect() {
    if (!this.enabledValue) return
    this.suggestions = {}
    this.fields = {}
    this._debounce = null
    this._touch =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches
    this._boundOnContextChange = this._onContextChange.bind(this)

    this.ghostFields.forEach((name) => this._setupField(name))
    this.element.addEventListener("input", this._boundOnContextChange)
    this.element.addEventListener("change", this._boundOnContextChange)
    this._scheduleFetch()
  }

  disconnect() {
    if (this._debounce) clearTimeout(this._debounce)
    this.element.removeEventListener("input", this._boundOnContextChange)
    this.element.removeEventListener("change", this._boundOnContextChange)
  }

  private _inputFor(
    name: GhostFieldName,
  ): HTMLInputElement | HTMLTextAreaElement | null {
    const fieldName = `${ENTITIES[this.entity].scope}[${name}]`
    return this.element.querySelector<HTMLInputElement | HTMLTextAreaElement>(
      `input[name="${fieldName}"], textarea[name="${fieldName}"]`,
    )
  }

  private _setupField(name: GhostFieldName) {
    const input = this._inputFor(name)
    const wrap = input?.parentElement
    if (!input || !wrap) return

    wrap.classList.add("relative")

    const ghost = document.createElement("div")
    ghost.setAttribute("aria-hidden", "true")
    ghost.dataset.bookingAiGhost = name
    // A textarea's ghost starts at its first line, an input's is centred.
    ghost.className =
      input instanceof HTMLTextAreaElement
        ? "pointer-events-none absolute inset-0 hidden items-start pt-2"
        : "pointer-events-none absolute inset-0 hidden items-center"
    const ghostText = document.createElement("span")
    ghostText.className = "truncate px-3 text-sm text-muted-foreground/60"
    ghost.appendChild(ghostText)

    const hint = document.createElement("button")
    hint.type = "button"
    hint.tabIndex = -1
    hint.dataset.bookingAiHint = name
    hint.className =
      "absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-1 " +
      "rounded px-1.5 py-0.5 text-[10px] font-medium text-violet-500"
    hint.textContent = this._touch ? this.acceptTapValue : this.acceptTabValue
    hint.addEventListener("click", () => this._accept(name))

    wrap.appendChild(ghost)
    wrap.appendChild(hint)
    input.addEventListener("keydown", (e) => this._onKeydown(name, e))

    this.fields[name] = { input, ghost, ghostText, hint }
  }

  private _onKeydown(name: GhostFieldName, event: KeyboardEvent) {
    const f = this.fields[name]
    if (!f || f.ghost.classList.contains("hidden")) return
    if (event.key === "Tab") {
      event.preventDefault()
      this._accept(name)
    } else if (event.key === "Escape") {
      this._dismiss(name)
    }
  }

  _onContextChange() {
    this._scheduleFetch()
  }

  private _scheduleFetch() {
    if (this._debounce) clearTimeout(this._debounce)
    this._debounce = setTimeout(() => this._fetch(), DEBOUNCE_MS)
  }

  private _context(): Record<string, unknown> {
    const value = (selector: string) =>
      this.element.querySelector<HTMLInputElement>(selector)?.value || ""
    if (this.entity === "Opportunity") {
      // React's OpportunityForm context. The drawer has no Stage field (React
      // sent the unset stage as null).
      return {
        company_id:
          value('input[name="opportunity[contact_company_id]"]') || null,
        stage: null,
        contact_person_id:
          value('input[name="opportunity[contact_person_id]"]') || null,
      }
    }
    return this._bookingContext()
  }

  private _bookingContext() {
    const value = (selector: string) =>
      this.element.querySelector<HTMLInputElement>(selector)?.value || ""
    const participantIds = Array.from(
      this.element.querySelectorAll<HTMLInputElement>(
        'input[name="booking[participant_ids][]"]',
      ),
    )
      .map((node) => node.value)
      .filter(Boolean)

    return {
      title: this._inputFor("title")?.value || "",
      location: this._inputFor("location")?.value || "",
      date: value('input[name="booking[start_time]"]'),
      task_id: value('input[name="booking[task_id]"]'),
      participant_ids: participantIds,
    }
  }

  private async _fetch() {
    try {
      const response = await post(SUGGEST_URL, {
        body: { entity_type: this.entity, context: this._context() },
        responseKind: "json",
      })
      if (!response.ok) return
      const data = (await response.json) as SuggestionsResponse
      this.suggestions = (data && data.suggestions) || {}
      this.ghostFields.forEach((name) => this._render(name))
    } catch {
      // Network/abort — suggestions are best-effort, never block the form.
    }
  }

  private _render(name: GhostFieldName) {
    const f = this.fields[name]
    if (!f) return
    const entry = this.suggestions[name]
    const text = entry && entry.suggestion
    // Only show a ghost when the field is empty — never overwrite typed input.
    if (text && !f.input.value) {
      f.ghostText.textContent = text
      this._toggle(f.ghost, true)
      this._toggle(f.hint, true)
    } else {
      this._hide(name)
    }
  }

  private _toggle(node: HTMLElement, show: boolean) {
    node.classList.toggle("hidden", !show)
    node.classList.toggle("flex", show)
  }

  private _hide(name: GhostFieldName) {
    const f = this.fields[name]
    if (!f) return
    this._toggle(f.ghost, false)
    this._toggle(f.hint, false)
  }

  private _accept(name: GhostFieldName) {
    const f = this.fields[name]
    const entry = this.suggestions[name]
    if (!f || !entry?.suggestion) return
    f.input.value = entry.suggestion
    // Fire input so validation + the unsaved-changes guard see the new value.
    f.input.dispatchEvent(new Event("input", { bubbles: true }))
    this._hide(name)
    this._feedback(name, "suggestion_accepted", entry)
    delete this.suggestions[name]
  }

  private _dismiss(name: GhostFieldName) {
    const entry = this.suggestions[name]
    this._hide(name)
    if (entry) this._feedback(name, "suggestion_dismissed", entry)
    delete this.suggestions[name]
  }

  private _feedback(
    name: GhostFieldName,
    interactionType: string,
    entry: SuggestionEntry,
  ) {
    void post(FEEDBACK_URL, {
      body: {
        interaction_type: interactionType,
        field_name: name,
        entity_type: this.entity,
        suggestion: entry.suggestion,
      },
      responseKind: "json",
    }).catch(() => undefined) // feedback is best-effort telemetry
  }
}
